import { Button, Typography } from '@breetometer/components';
import { yupResolver } from '@hookform/resolvers/yup';
import { Otp, OtpForm } from 'BreetComponents';
import { handleApiError, OTPLENGTH, PINLENGTH, showToast } from 'BreetHelpers';
import { useGenerateOTPQuery, useResetPinMutation } from 'BreetRedux';
import { type FormEvent, useCallback } from 'react';
import { useForm } from 'react-hook-form';

import { type IFormPin, ResetPinSchema, type ResetPinSchemaType } from './FormPin.helpers';

const FormPinReset = ({ onSuccess }: IFormPin) => {
	const [resetPin, { isLoading: resetPinLoading }] = useResetPinMutation();
	const { refetch, isLoading: generateOtpLoading } = useGenerateOTPQuery();

	const resetHookForm = useForm({
		resolver: yupResolver(ResetPinSchema),
		defaultValues: { pin: '', otp: '' },
	});

	const onChangeUpdateInput = useCallback(
		(key: 'pin' | 'otp') => (valueArg: string) => {
			resetHookForm.setValue(key, valueArg, { shouldValidate: true, shouldDirty: true });
		},
		[resetHookForm]
	);

	const handlePinReset = useCallback(
		(data: ResetPinSchemaType) => {
			resetPin({ otp: data.otp, pin: data.pin })
				.unwrap()
				.then(() => {
					showToast('success', 'PIN code updated successfully.');
					onSuccess?.();
				})
				.catch((e: unknown) => {
					resetHookForm.setError('otp', { message: 'Incorrect or expired OTP' });
					handleApiError(e);
				});
		},
		[onSuccess, resetHookForm, resetPin]
	);

	const handleFormSubmit = useCallback(
		async (e: FormEvent<HTMLFormElement>) => {
			await resetHookForm.handleSubmit(handlePinReset)(e);
		},
		[handlePinReset, resetHookForm]
	);

	const onResendOtp = useCallback(
		(restartCountDown?: () => void) => {
			refetch()
				.unwrap()
				.then(() => {
					restartCountDown?.();
				})
				.catch(handleApiError);
		},
		[refetch]
	);

	const disableBtn = !resetHookForm.formState.isValid;

	return (
		<div className='pinverification reset-otp'>
			<div className='pinverification_header'>
				<Typography
					as='h1'
					className='color-neutral-50'
				>
					Almost Done
				</Typography>

				<Typography className='color-primary-50'>
					Enter the {OTPLENGTH}-digit OTP sent to the email associated with this account.
				</Typography>
			</div>

			<form onSubmit={handleFormSubmit}>
				<Typography
					as='label'
					className='color-primary-50'
				>
					Enter OTP
				</Typography>

				<OtpForm
					otpLength={OTPLENGTH}
					hideButton
					onChange={onChangeUpdateInput('otp')}
					errorMessage={resetHookForm.formState.errors.otp?.message}
					value={resetHookForm.watch('otp')}
					onResend={onResendOtp}
					resendOtpLoading={generateOtpLoading}
					resendOtp
				/>

				<Typography
					as='label'
					className='color-primary-50'
				>
					Create New PIN
				</Typography>

				<Otp.Input
					otpLength={PINLENGTH}
					position='center'
					onChange={onChangeUpdateInput('pin')}
					error={resetHookForm.formState.errors.pin?.message}
					id='pin'
					showError
					data-testid='pin-input'
				/>

				<Button
					label='Save'
					disabled={disableBtn}
					type='submit'
					isLoading={resetPinLoading}
					className='primary-hover'
				/>
			</form>
		</div>
	);
};

export default FormPinReset;
